import { takeUntil } from "rxjs/operators";
import { Observable, Subject, fromEvent } from "rxjs";
import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { debounceTime, throttleTime } from "rxjs/operators";

import { AuthService } from "src/app/core/services";
import { DashboardService } from "src/app/commons/services";
import { DocumentRefService } from "../utils";
import { IAuthUserSession } from "src/app/core/ITypes";

@Component({
  selector: "sv-navbar",
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.scss"],
  providers: [DocumentRefService],
})
export class NavbarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('headerContainer', { static: false }) headerContainer: ElementRef;
  
  // defaults
  isActionDropdownVisible = false;
  username: string = "Welcome";
  storeName: string = null;
  storeUrl: string = null;
  authUser$: Observable<IAuthUserSession>;
  authUser;
  isNavbarCollapsed = true;

  private resizeObserver: ResizeObserver;
  private destroyed$ = new Subject<void>();

  constructor(
    private route: Router,
    private authService: AuthService,
    private document: DocumentRefService,
    private dashboardService: DashboardService
  ) {
    this.authUser = this.authService.authUser;
    this.authUser$ = this.authService.authUser$;

    if (this.authUser) {
      this.username = this.authUser.name;
      this.storeName = this.authUser.store.label;
      this.storeUrl = this.sanitizeUrl(this.authUser.storeUrl);
    }

    this.dashboardService.sidebarSubscriber$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val) => {
        if (val) {
          this.toggleSideMenu();
        }
      });
  }

  ngOnInit(): void {}

  ngAfterViewInit() {
    this.initializeHeaderHeightTracking();
  }

  toggleActionDropdown() {
    this.isActionDropdownVisible = !this.isActionDropdownVisible;
  }

  handleLogout() {
    this.authService.logout();
    this.route.navigate(["/"]);
    this.closeNavbar();
  }

  toggleNavbar(opt = null) {
    this.isNavbarCollapsed = !this.isNavbarCollapsed;
  }

  closeNavbar() {
    this.isNavbarCollapsed = true;
    this.isActionDropdownVisible = false;
  }

  toggleSideMenu() {
    const layoutElement = this.document
      .get()
      .querySelector("sv-layout .layout");
    const sideBarElement = this.document.get().querySelector("#sidebar");
    const sideMenuIconElement = this.document
      .get()
      .querySelector(".nav-mobile-menu-btn.side-menu-btn");

    if (sideBarElement) {
      if (sideBarElement.classList.contains("active")) {
        sideBarElement.classList.remove("active");
        layoutElement.classList.remove("side-menu--open");
        sideMenuIconElement.classList.remove("open");
      } else {
        sideBarElement.classList.add("active");
        layoutElement.classList.add("side-menu--open");
        sideMenuIconElement.classList.add("open");
      }
    }
  }

  private initializeHeaderHeightTracking() {
    const headerElement = document.querySelector('.c-appHeaderContainer') as HTMLElement;
    
    if (headerElement) {
      // Set initial height
      this.updateHeaderHeight(headerElement);
      
      // Use ResizeObserver for efficient resize detection
      this.resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          this.updateHeaderHeight(entry.target as HTMLElement);
        }
      });
      
      this.resizeObserver.observe(headerElement);
      
      // Listen to window resize as backup
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(16), // ~60fps
          takeUntil(this.destroyed$)
        )
        .subscribe(() => {
          this.updateHeaderHeight(headerElement);
        });
      
      // Listen to scroll events if header height changes on scroll
      fromEvent(window, 'scroll')
        .pipe(
          throttleTime(16), // ~60fps
          takeUntil(this.destroyed$)
        )
        .subscribe(() => {
          this.updateHeaderHeight(headerElement);
        });
    }
  }

  private updateHeaderHeight(element: HTMLElement) {
    const height = element.offsetHeight;
    document.documentElement.style.setProperty('--headerHeight', `${height}px`);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private sanitizeUrl(url: string): string {
    if (!url) return url;

    let sanitized = url.replace(/\s+/g, "");

    if (!/^https?:\/\//i.test(sanitized)) {
      if (/^https?:/i.test(sanitized)) {
        sanitized = sanitized.replace(/^(https?):/i, "$1://");
      } else {
        sanitized = "https://" + sanitized;
      }
    }

    return sanitized;
  }
}
